import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, HomeIcon, SearchIcon, ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
interface PartnerBranding {
  name: string;
  logo: string;
  primaryColor: string;
  accentColor: string;
  backUrl: string;
  programTitle?: string;
}
export function ThankYouPage() {
  usePageMeta('Thank You | findonlinecollege.com', 'Your information request has been submitted. A school representative will be in touch shortly.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website',
    // Confirmation screen — only reachable after submitting, nothing to index.
    noindex: true
  });
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as any)?.formData;
  const partner = (location.state as any)?.partner as PartnerBranding | undefined;
  if (partner) {
    return <main className="min-h-screen flex flex-col" style={{
      backgroundColor: '#F8FAFB'
    }} data-id="element-2563" data-mp="3mu72k64tey1g">
        {/* Partner Header */}
        <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm border-b border-gray-100" data-id="element-2564" data-mp="3vtbzj80qajbi">
          {partner.logo ? <img src={partner.logo} alt={`${partner.name} Logo`} className="h-14 object-contain" data-id="element-2565" data-mp="4focclsqr8f8" /> : <span className="text-xl font-bold" style={{
          color: partner.primaryColor
        }} data-id="element-2566" data-mp="9eiudlivwsya">
              {partner.name}
            </span>}
        </header>

        {/* Hero */}
        <div className="relative overflow-hidden py-16 md:py-24" style={{
        background: `linear-gradient(135deg, ${partner.primaryColor} 0%, ${partner.primaryColor}dd 60%, ${partner.accentColor}88 100%)`
      }} data-id="element-2567" data-mp="jbdto6mzexdm">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2568" data-mp="2l4bmi2grdpyk">
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.1
          }} className="mx-auto mb-6" data-id="element-2569" data-mp="2p5wy7khk3y76">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto border-2" style={{
              backgroundColor: `${partner.accentColor}20`,
              borderColor: `${partner.accentColor}40`
            }} data-id="element-2570" data-mp="5cmroyb9t87f">
                <CheckCircleIcon className="w-10 h-10" style={{
                color: partner.accentColor
              }} data-id="element-2571" data-mp="aa69g876g344" />
              </div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2572" data-mp="oy9gzm0vbmv1">
              Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="text-lg text-white/90 mb-2" data-id="element-2573" data-mp="1cnh9lg93syah">
              Your information has been submitted to{' '}
              <strong className="text-white" data-id="element-2574" data-mp="17m1q3smkqzu7">{partner.name}</strong>.
            </motion.p>

            {partner.programTitle && <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-base text-white/70" data-id="element-2575" data-mp="tun6pkor8wmm">
                Program: {partner.programTitle}
              </motion.p>}

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} className="text-base text-white/70 max-w-md mx-auto mt-3" data-id="element-2576" data-mp="ousi5nsgcj4q">
              A representative from {partner.name} will reach out to you shortly
              to discuss your education options.
            </motion.p>
          </div>
        </div>

        {/* Steps Card */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16 w-full" data-id="element-2577" data-mp="3u9irq2iamiyp">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.55
        }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2578" data-mp="3q8yvd15jnjdm">
            <h2 className="text-lg font-bold mb-4" style={{
            color: partner.primaryColor
          }} data-id="element-2579" data-mp="3gdndwb2r3y26">
              What Happens Next?
            </h2>
            <div className="space-y-4" data-id="element-2580" data-mp="37c72q1443rww">
              <div className="flex items-start gap-4" data-id="element-2581" data-mp="19kuobv2iplo9">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2582" data-mp="19kb6eecvaxa8">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2583" data-mp="2ng303k50thxy">
                    1
                  </span>
                </div>
                <div data-id="element-2584" data-mp="2x7z1b42elo6e">
                  <p className="font-semibold text-gray-800" data-id="element-2585" data-mp="2x8sdikk4t5vx">
                    Application Review
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2586" data-mp="24i0ab34nfgz3">
                    {partner.name} will review your information and match you
                    with the best program options.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2587" data-mp="2dhx39gy2n1zy">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2588" data-mp="2ihjg3p9zmg88">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2589" data-mp="3twyf0ylrbi5z">
                    2
                  </span>
                </div>
                <div data-id="element-2590" data-mp="1pt3s5pmaf1cu">
                  <p className="font-semibold text-gray-800" data-id="element-2591" data-mp="1ptvd86i9dgov">
                    Expect a Call or Email
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2592" data-mp="23rjvd6kxy9q8">
                    An admissions advisor from {partner.name} will contact you
                    {formData?.email ? ` at ${formData.email}` : ''} to discuss
                    next steps.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2593" data-mp="166p8tviqqx12">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2594" data-mp="1kuispsev0lpg">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2595" data-mp="sd6dcicvcoi4">
                    3
                  </span>
                </div>
                <div data-id="element-2596" data-mp="2z1gii1q1ns9r">
                  <p className="font-semibold text-gray-800" data-id="element-2597" data-mp="2z0xabcq74i8x">
                    Enroll & Get Started
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2598" data-mp="33tzvoxktkle1">
                    Once you're ready, your advisor at {partner.name} will guide
                    you through the enrollment process.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2599" data-mp="3htj4ztpda2rx">
              <button onClick={() => navigate(partner.backUrl)} className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-md transition-colors text-sm" style={{
              backgroundColor: partner.primaryColor
            }} onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'} data-id="element-2600" data-mp="3mras8af2h0ps">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2601" data-mp="1xez7xf5y79ij" />
                Back to {partner.name}
              </button>
              <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 border font-semibold rounded-md transition-colors text-sm" style={{
              borderColor: partner.primaryColor,
              color: partner.primaryColor
            }} data-id="element-2602" data-mp="2t1sv7n903o1i">
                <HomeIcon className="w-4 h-4" data-id="element-2603" data-mp="2k2xpec9vcfyq" />
                Explore More Schools
              </button>
            </div>
          </motion.div>
        </div>
      </main>;
  }
  // Default generic thank you page
  return <main className="flex-grow bg-light" data-id="element-2604" data-mp="37qxj9l2h8mhj">
      <div className="hero-bg relative overflow-hidden py-16 md:py-24" data-id="element-2605" data-mp="37s0aju3poqxz">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2606" data-mp="32z73gzkrt6iq">
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }} className="mx-auto mb-6" data-id="element-2607" data-mp="2y1e00eauqqm5">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto border border-white/20" data-id="element-2608" data-mp="1vtmanyxtdrw">
              <CheckCircleIcon className="w-10 h-10 text-gold" data-id="element-2609" data-mp="6un24n1p5gv8" />
            </div>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2610" data-mp="bs6fzluhfrmn">
            Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35
        }} className="text-lg text-gray-300 mb-2" data-id="element-2611" data-mp="ujfr1zjwiogc">
            Your information has been submitted successfully.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45
        }} className="text-base text-gray-400 max-w-md mx-auto" data-id="element-2612" data-mp="18iyz6scppzsn">
            A representative from one of our partner schools will reach out to
            you shortly to discuss your education options.
          </motion.p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16" data-id="element-2613" data-mp="9vmzhno3pj0v">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.55
      }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2614" data-mp="esde06k9c6k6">
          <h2 className="text-lg font-bold text-navy mb-4" data-id="element-2615" data-mp="numo4vbm5xx1">
            What Happens Next?
          </h2>
          <div className="space-y-4" data-id="element-2616" data-mp="ss4xunxggh63">
            <div className="flex items-start gap-4" data-id="element-2617" data-mp="2plg8rx0gl5bw">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2618" data-mp="2pn143oulksat">
                <span className="text-sm font-bold text-navy" data-id="element-2619" data-mp="2um4959akuyis">1</span>
              </div>
              <div data-id="element-2620" data-mp="2n2uzdey5sxt7">
                <p className="font-semibold text-gray-800" data-id="element-2621" data-mp="2ia1wyxq5y1ka">
                  Review Your Matches
                </p>
                <p className="text-sm text-gray-600" data-id="element-2622" data-mp="2dafh0giya77s">
                  Our system is matching you with accredited programs that fit
                  your goals and preferences.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2623" data-mp="35yb9f91hlcyr">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2624" data-mp="310t21n34b5sv">
                <span className="text-sm font-bold text-navy" data-id="element-2625" data-mp="2w0nf2k7sm7h4">2</span>
              </div>
              <div data-id="element-2626" data-mp="2r2vez2wzvf1u">
                <p className="font-semibold text-gray-800" data-id="element-2627" data-mp="2r1bypcbc9402">
                  Expect a Call or Email
                </p>
                <p className="text-sm text-gray-600" data-id="element-2628" data-mp="1knuohcqqnp2n">
                  An admissions advisor from a matched school will contact you
                  {formData?.email ? ` at ${formData.email}` : ''} to discuss
                  next steps.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2629" data-mp="botc0xm4m61x">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2630" data-mp="3tvhe0elvqd3d">
                <span className="text-sm font-bold text-navy" data-id="element-2631" data-mp="2nlnv8dbqllk">3</span>
              </div>
              <div data-id="element-2632" data-mp="3k4whqxtohknm">
                <p className="font-semibold text-gray-800" data-id="element-2633" data-mp="3k4czfg048u92">
                  Enroll & Get Started
                </p>
                <p className="text-sm text-gray-600" data-id="element-2634" data-mp="3ox63e1nrfyk3">
                  Once you've found the right fit, your advisor will guide you
                  through the enrollment process.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2635" data-mp="315sek5otil3v">
            <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 bg-navy hover:bg-navy-light text-white font-semibold rounded-md transition-colors text-sm" data-id="element-2636" data-mp="318mxttw3hzvp">
              <HomeIcon className="w-4 h-4" data-id="element-2637" data-mp="2ihzy1e0s85uo" />
              Back to Home
            </button>
            <button onClick={() => navigate('/colleges')} className="flex items-center justify-center gap-2 px-6 py-3 border border-navy text-navy hover:bg-navy/5 font-semibold rounded-md transition-colors text-sm" data-id="element-2638" data-mp="2ikcranbzq2n1">
              <SearchIcon className="w-4 h-4" data-id="element-2639" data-mp="35i7jt0f2rfjc" />
              Browse More Schools
            </button>
          </div>
        </motion.div>
      </div>
    </main>;
}